import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { HexColorSchema } from './query-statuses.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Query Sub Status master (Masters → Query Sub Statuses). A finer breakdown of
 * a Query Status (e.g. status "Quoted" → sub status "Quoted - negotiation"),
 * held in its own table (parallel to Query Sub Sources) - distinct from the
 * self-referential hierarchy on Query Status. FK to `query_statuses`; `code` is
 * unique per parent status, not globally. See ADR-0016 / ADR-0028.
 */

/** Slim parent (query status) reference embedded in list/detail rows. */
const QueryStatusRefSchema = z.object({ id: UuidSchema, code: z.string(), name: z.string() });

export const QuerySubStatusListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  queryStatus: QueryStatusRefSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Editable hex color; NULL → inherit parent status's color (or inference). */
  color: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type QuerySubStatusListItem = z.infer<typeof QuerySubStatusListItemSchema>;

export const QuerySubStatusListQuerySchema = PaginationQuerySchema.extend({
  queryStatusId: UuidSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type QuerySubStatusListQuery = z.infer<typeof QuerySubStatusListQuerySchema>;

export const QuerySubStatusListResponseSchema = paginatedResponseSchema(
  QuerySubStatusListItemSchema,
);
export type QuerySubStatusListResponse = z.infer<typeof QuerySubStatusListResponseSchema>;

export const QuerySubStatusDetailSchema = QuerySubStatusListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type QuerySubStatusDetail = z.infer<typeof QuerySubStatusDetailSchema>;

const QuerySubStatusCoreFieldsSchema = z.object({
  queryStatusId: UuidSchema.describe('Parent query status UUID. Use GET /query-statuses/options.'),
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  /** Stage-indicator hex color; NULL inherits from parent. */
  color: HexColorSchema.nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateQuerySubStatusRequestSchema = QuerySubStatusCoreFieldsSchema;
export type CreateQuerySubStatusRequest = z.infer<typeof CreateQuerySubStatusRequestSchema>;

export const UpdateQuerySubStatusRequestSchema = QuerySubStatusCoreFieldsSchema.partial();
export type UpdateQuerySubStatusRequest = z.infer<typeof UpdateQuerySubStatusRequestSchema>;
